import React, { useState } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    FormControlLabel,
    Typography,
    Box,
    Divider
} from '@mui/material';
import { useTranslation } from 'react-i18next';

const ConsentForm = ({ open, onClose, onAgree }) => {
    const { t, i18n } = useTranslation();
    const [agreed, setAgreed] = useState(false);
    const isGerman = i18n.language === 'de';

    const handleAgree = () => {
        if (agreed) {
            onAgree();
            onClose();
        }
    };

    const consentContent = {
        en: {
            title: "User Consent Form – Data Collection & Participation in Research",
            intro: "By using this system and optionally participating in research activities, you consent to the collection, processing, and use of your personal data as outlined below.",
            sections: [
                {
                    title: "1. Purpose of Data Collection",
                    content: "Your data will be used to support system functionality, user experience improvement, and research purposes such as data analysis and evaluation of behavioral or physiological patterns."
                },
                {
                    title: "2. Types of Data Collected",
                    content: "• Body metrics (e.g., weight, heart rate, activity level)\n• Performance scores\n• Sleep data (duration, bedtime, wake time)\n• Contact information (e.g., name, email, address)\n• Other data provided during participation"
                },
                {
                    title: "3. Data Usage and Sharing",
                    content: "Collected data may be:\n• Used to improve the system and conduct research\n• Anonymized and analyzed for scientific or development purposes\n• Shared only with authorized personnel or research partners under strict confidentiality"
                },
                {
                    title: "4. Participation in Experimental Studies",
                    content: "Participation in any experimental or research component is voluntary and optional. You may withdraw at any time without consequences."
                },
                {
                    title: "5. Legal Disclaimers",
                    content: "• Your data will be handled in accordance with applicable data protection laws (e.g., GDPR).\n• The provider is not liable for any decisions made based on data output unless stated otherwise.\n• You agree that participation does not constitute a medical diagnosis or treatment.\n• You may be contacted for follow-up or clarification regarding your data or participation."
                },
                {
                    title: "6. Your Rights",
                    content: "You have the right to access, correct, or request deletion of your personal data. You may withdraw your consent at any time without penalty."
                }
            ],
            checkboxLabel: "I consent to the collection, processing, and use of my data as described above.",
            agreeButton: "I Agree",
            cancelButton: "Cancel"
        },
        de: {
            title: "Einverständniserklärung – Datenerhebung & Teilnahme an der Forschung",
            intro: "Durch die Nutzung dieses Systems und die optionale Teilnahme an Forschungsaktivitäten stimmen Sie der Erhebung, Verarbeitung und Nutzung Ihrer personenbezogenen Daten wie unten beschrieben zu.",
            sections: [
                {
                    title: "1. Zweck der Datenerhebung",
                    content: "Ihre Daten werden verwendet, um die Systemfunktionalität zu unterstützen, die Benutzererfahrung zu verbessern und für Forschungszwecke wie Datenanalyse und Bewertung von Verhaltens- oder physiologischen Mustern."
                },
                {
                    title: "2. Arten der erhobenen Daten",
                    content: "• Körpermetriken (z.B. Gewicht, Herzfrequenz, Aktivitätslevel)\n• Leistungswerte\n• Schlafdaten (Dauer, Schlafenszeit, Aufwachzeit)\n• Kontaktinformationen (z.B. Name, E-Mail, Adresse)\n• Andere während der Teilnahme bereitgestellte Daten"
                },
                {
                    title: "3. Datennutzung und -weitergabe",
                    content: "Erhobene Daten können:\n• Zur Verbesserung des Systems und zur Durchführung von Forschung verwendet werden\n• Anonymisiert und für wissenschaftliche oder Entwicklungszwecke analysiert werden\n• Nur mit autorisiertem Personal oder Forschungspartnern unter strikter Vertraulichkeit geteilt werden"
                },
                {
                    title: "4. Teilnahme an experimentellen Studien",
                    content: "Die Teilnahme an experimentellen oder Forschungskomponenten ist freiwillig und optional. Sie können jederzeit ohne Konsequenzen zurücktreten."
                },
                {
                    title: "5. Rechtliche Hinweise",
                    content: "• Ihre Daten werden gemäß den geltenden Datenschutzgesetzen (z.B. DSGVO) behandelt.\n• Der Anbieter haftet nicht für Entscheidungen, die auf Basis der Datenausgabe getroffen werden, sofern nicht anders angegeben.\n• Sie stimmen zu, dass die Teilnahme keine medizinische Diagnose oder Behandlung darstellt.\n• Sie können zur Nachverfolgung oder Klärung bezüglich Ihrer Daten oder Teilnahme kontaktiert werden."
                },
                {
                    title: "6. Ihre Rechte",
                    content: "Sie haben das Recht auf Zugang, Berichtigung oder Löschung Ihrer personenbezogenen Daten. Sie können Ihre Einwilligung jederzeit ohne Nachteile widerrufen."
                }
            ],
            checkboxLabel: "Ich stimme der Erhebung, Verarbeitung und Nutzung meiner Daten wie oben beschrieben zu.",
            agreeButton: "Ich stimme zu",
            cancelButton: "Abbrechen"
        }
    };

    const content = isGerman ? consentContent.de : consentContent.en;

    return (
        <Dialog
            open={open}
            onClose={() => {}}
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    maxHeight: '80vh'
                }
            }}
        >
            <DialogTitle sx={{ 
                fontWeight: 'bold',
                fontSize: '1.3rem',
                color: '#1976d2',
                borderBottom: '2px solid #e0e0e0',
                pb: 2
            }}>
                {content.title}
            </DialogTitle>
            
            <DialogContent sx={{ mt: 2 }}>
                <Typography variant="body1" paragraph sx={{ fontStyle: 'italic', mb: 3 }}>
                    {content.intro}
                </Typography>

                {content.sections.map((section, index) => (
                    <Box key={index} sx={{ mb: 3 }}>
                        <Typography variant="h6" sx={{ 
                            fontWeight: 'bold', 
                            color: '#424242',
                            mb: 1 
                        }}>
                            {section.title}
                        </Typography>
                        <Typography 
                            variant="body2" 
                            sx={{ 
                                whiteSpace: 'pre-line',
                                lineHeight: 1.6,
                                pl: 2
                            }}
                        >
                            {section.content}
                        </Typography>
                        {index < content.sections.length - 1 && (
                            <Divider sx={{ mt: 2 }} />
                        )}
                    </Box>
                ))}

                <Box sx={{ 
                    mt: 4, 
                    p: 2, 
                    backgroundColor: '#f5f5f5',
                    borderRadius: 1,
                    border: '1px solid #e0e0e0'
                }}>
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={agreed}
                                onChange={(e) => setAgreed(e.target.checked)}
                                color="primary"
                            />
                        }
                        label={
                            <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                                ✅ {content.checkboxLabel}
                            </Typography>
                        }
                    />
                </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2, borderTop: '1px solid #e0e0e0' }}>
                <Button 
                    onClick={onClose} 
                    color="secondary"
                    variant="outlined"
                >
                    {content.cancelButton}
                </Button>
                <Button 
                    onClick={handleAgree} 
                    color="primary"
                    variant="contained"
                    disabled={!agreed}
                >
                    {content.agreeButton}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default ConsentForm;